import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import { matchesFilters } from './helpers/filterHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

const performMessageApiCreateRequest = pendingMessage => {
  const { conversation_id: conversationId, id } = pendingMessage;

  if (hasMessageFailedWithExternalError(pendingMessage)) {
    return MessageApi.retry(conversationId, id);
  }
  return MessageApi.create(pendingMessage);
};

const updateMessageContent = async payload => {
  const { data } = await MessageApi.updateContent(
    payload.conversationId,
    payload.editedMessage.id,
    {
      content: payload.content,
      replacedAttachments: payload.replacedAttachments,
    }
  );
  return data;
};

const handleMessageSuccess = (commit, responseData) => {
  commit(types.ADD_MESSAGE, {
    ...responseData,
    status: MESSAGE_STATUS.SENT,
  });
  commit(types.ADD_CONVERSATION_ATTACHMENTS, {
    ...responseData,
    status: MESSAGE_STATUS.SENT,
  });
};

const handleMessageUpdateSuccess = (commit, responseData) => {
  commit(types.ADD_MESSAGE, {
    ...responseData,
    status: responseData.status || MESSAGE_STATUS.SENT,
  });

  commit(types.ADD_CONVERSATION_ATTACHMENTS, responseData);
  return responseData;
};

const handleMessageError = (commit, pendingMessage, error) => {
  const errorMessage = error.response?.data?.error;

  commit(types.ADD_MESSAGE, {
    ...pendingMessage,
    meta: { error: errorMessage },
    status: MESSAGE_STATUS.FAILED,
  });
};

const handleMessageUpdateError = (
  commit,
  editedMessage,
  originalContent,
  error
) => {
  const errorMessage = error.response?.data?.error;

  commit(types.ADD_MESSAGE, {
    ...editedMessage,
    content: originalContent,
    meta: { error: errorMessage },
    status: MESSAGE_STATUS.SENT,
  });
};

const sortMessages = messages =>
  [...messages].sort((a, b) => {
    const createdAtA = a.created_at || 0;
    const createdAtB = b.created_at || 0;

    if (createdAtA === createdAtB) {
      return a.id - b.id;
    }

    return createdAtA - createdAtB;
  });

const mergeMessages = (existingMessages = [], newMessages = []) => {
  const messagesById = new Map(
    existingMessages.map(message => [message.id, message])
  );
  newMessages.forEach(message => messagesById.set(message.id, message));

  return sortMessages([...messagesById.values()]);
};

const SERVER_FILTER_ONLY_KEYS = ['message_content', 'manager_chat_presence'];

const hasServerFilterOnlyConditions = filters => {
  return filters.some(filter =>
    SERVER_FILTER_ONLY_KEYS.includes(filter.attribute_key)
  );
};

const getActiveFolderFilters = rootState => {
  return rootState?.customViews?.activeConversationFolder?.query?.payload || [];
};

const shouldSkipFilteredConversationUpdate = (
  state,
  rootState,
  conversation
) => {
  const isConversationInList = state?.allConversations?.some(
    item => item.id === conversation.id
  );
  if (isConversationInList) {
    return false;
  }

  const activeFilters = state?.appliedFilters?.length
    ? state.appliedFilters
    : getActiveFolderFilters(rootState);
  const hasActiveFolder = rootState?.route && isOnFoldersView(rootState);

  if (!activeFilters.length) {
    return !!hasActiveFolder;
  }

  if (hasServerFilterOnlyConditions(activeFilters)) {
    return true;
  }

  return !matchesFilters(conversation, activeFilters);
};

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch }) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const params = state.conversationFilters;
      const {
        data: { data },
      } = await ConversationApi.get(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
    } catch (error) {
      // Handle error
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Handle error
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length && !data.after) {
        commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchLatestMessages: async ({ commit, state }, { conversationId, merge }) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({ conversationId });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });

      const selectedChat = state.allConversations.find(
        conversation => conversation.id === conversationId
      );
      const messages =
        merge && selectedChat
          ? mergeMessages(selectedChat.messages, payload)
          : payload;

      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: messages,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED, conversationId);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchScheduledMessages: async ({ commit }, conversationId) => {
    try {
      const {
        data: { payload },
      } = await MessageApi.getScheduledMessages(conversationId);

      // Закидываем все запланированные сообщения в общий массив диалога
      if (payload && payload.length > 0) {
        payload.forEach(message => {
          commit(types.ADD_MESSAGE, message);
        });
      }
    } catch (error) {
      // Игнорируем ошибку, если не удалось загрузить
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;

    if (!lastMessageId) return;

    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: mergeMessages(messages, missingMessages),
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED, data.id);
    const needsFetch =
      data.dataFetched !== true || data.has_pending_silent === true;
    if (needsFetch) {
      try {
        const firstId = data.messages?.[0]?.id;

        if (after && firstId) {
          await dispatch('fetchPreviousMessages', {
            after,
            before: firstId,
            conversationId: data.id,
          });
        } else if (firstId && data.dataFetched !== true) {
          await dispatch('fetchPreviousMessages', {
            before: firstId,
            conversationId: data.id,
          });
          await dispatch('fetchLatestMessages', {
            conversationId: data.id,
            merge: true,
          });
        } else {
          await dispatch('fetchLatestMessages', {
            conversationId: data.id,
            merge: data.dataFetched === true,
          });
        }

        await dispatch('fetchScheduledMessages', data.id);

        commit(types.SET_CHAT_DATA_FETCHED, data.id);
      } catch (error) {
        // Ignore error
      }
    }

    // silent messages persist after reload
    try {
      await dispatch('setConversationLastMessageId', {
        conversationId: data.id,
      });
      await dispatch('syncActiveConversationMessages', {
        conversationId: data.id,
      });
    } catch (error) {
      // Ignore error
    }
  },

  assignAgent: async ({ dispatch }, { conversationId, agentId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', response.data);
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, assignee) {
    commit(types.ASSIGN_AGENT, assignee);
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null }
  ) => {
    try {
      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const isScheduled = !!(
      pendingMessage.scheduled_at || pendingMessage.scheduledAt
    );

    commit(types.ADD_MESSAGE, {
      ...pendingMessage,
      status: isScheduled ? 'scheduled' : MESSAGE_STATUS.PROGRESS,
    });

    try {
      const response = await performMessageApiCreateRequest(pendingMessage);

      if (response.data.status === MESSAGE_STATUS.SCHEDULED) {
        commit(types.ADD_MESSAGE, response.data);
        commit(types.ADD_CONVERSATION_ATTACHMENTS, response.data);
      } else {
        handleMessageSuccess(commit, response.data);
      }
    } catch (error) {
      handleMessageError(commit, pendingMessage, error);
      throw error;
    }
  },

  addMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
  },

  editMessage: async ({ commit }, payload) => {
    const editedMessage = payload.editedMessage;
    const originalContent = editedMessage.content;
    const isScheduled = editedMessage.status === MESSAGE_STATUS.SCHEDULED;

    commit(types.ADD_MESSAGE, {
      ...editedMessage,
      content: payload.content,
      status: isScheduled ? MESSAGE_STATUS.SCHEDULED : MESSAGE_STATUS.PROGRESS,
    });

    try {
      const response = await updateMessageContent(payload);
      return handleMessageUpdateSuccess(commit, response);
    } catch (error) {
      handleMessageUpdateError(commit, editedMessage, originalContent, error);
      throw error;
    }
  },

  rescheduleMessage: async (
    { commit },
    { conversationId, messageId, scheduledAt }
  ) => {
    try {
      const { data } = await MessageApi.updateSchedule(
        conversationId,
        messageId,
        scheduledAt
      );

      commit(types.ADD_MESSAGE, data);
      return data;
    } catch (error) {
      throw new Error(error);
    }
  },

  updateMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      dispatch('conversationStats/get', {}, { root: true });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch, state, rootState }, conversation) {
    if (shouldSkipFilteredConversationUpdate(state, rootState, conversation)) {
      return;
    }

    const {
      meta: { sender },
    } = conversation;
    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
